import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { StudyService } from '../../../shared/study/service/study.service';
import { ParamContext } from '../../../shared/service/param.context';
import { StudyType } from '../../../shared/model/study-type.model';
import { StudyEditorContext } from '../../study-editor.context';
import { BasicDetails } from './basic-details.model';
import { Principal } from '../../../shared';
import { CREATE_STUDIES_PERMISSIONS, STUDY_FOLDER_ACTIONS_CHANGE_PERMISSION } from '../../../shared/auth/permissions';
import { StudyManagerTreeComponent } from '../../../study-manager/study-manager-tree.component';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DateHelperService } from '../../../shared/service/date.helper.service';
import { UpdateStudyBasicDetailsRequest } from '../../../shared/study/model/update-study-basic-details-request.model';
import { AlertService } from '../../../shared/alert/alert.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
    selector: 'jhi-basic-details',
    templateUrl: './basic-details.component.html',
    styleUrls: ['./basic-details.component.scss']
})
export class BasicDetailsComponent implements OnInit {

    CREATE_STUDIES_PERMISSIONS = CREATE_STUDIES_PERMISSIONS;
    STUDY_FOLDER_ACTIONS_CHANGE_PERMISSIONS = STUDY_FOLDER_ACTIONS_CHANGE_PERMISSION;

    private autoSaveSubject = new Subject<{ field: keyof UpdateStudyBasicDetailsRequest, value: any }>();
    private readonly AUTOSAVE_DELAY = 1000; // 1 second delay

    @Input()
        studyId: number;

    basicDetails: BasicDetails;
    basicDetailsInitialData: BasicDetails;
    studyTypeSelected: number | null = null;
    studyTypes: StudyType[] = [];

    constructor(private principal: Principal,
                public translateService: TranslateService,
                private studyService: StudyService,
                private paramContext: ParamContext,
                private studyEditorContext: StudyEditorContext,
                private modalService: NgbModal,
                private activeModal: NgbActiveModal,
                private alertService: AlertService,
                private dateHelperService: DateHelperService) {
        this.basicDetails = new BasicDetails();

        // Setup auto-save subscription
        this.autoSaveSubject.pipe(
            debounceTime(this.AUTOSAVE_DELAY),
            distinctUntilChanged((prev, curr) => {
                // Compare both field and value
                return prev.field === curr.field &&
                    JSON.stringify(prev.value) === JSON.stringify(curr.value);
            })
        ).subscribe((data) => {
            this.autoSaveStudyDetails(data.field, data.value);
        });

    }

    async ngOnInit(): Promise<void> {
        this.studyService.getStudyTypes(this.paramContext.cropName, this.paramContext.programUUID).toPromise().then(response => {
            this.studyTypes = response;
        });
        // Retrieve the Study Details
        const studyDetailsResponse = await this.studyService.getStudyDetails(this.studyId).toPromise();
        this.studyEditorContext.studyDetails = studyDetailsResponse.body;
        if (this.studyEditorContext.studyDetails) {
            this.basicDetails.name = this.studyEditorContext.studyDetails.name;
            this.basicDetails.objective = this.studyEditorContext.studyDetails.objective;
            this.basicDetails.description = this.studyEditorContext.studyDetails.description;
            this.basicDetails.studyType = this.studyEditorContext.studyDetails.studyType;
            this.basicDetails.startDate = this.dateHelperService.convertFormattedDateStringToNgbDate(this.studyEditorContext.studyDetails.startDate?.toString(), DateHelperService.YYYY_MM_DD_DASH_FORMAT);
            if (this.studyEditorContext.studyDetails.endDate) {
                this.basicDetails.endDate = this.dateHelperService.convertFormattedDateStringToNgbDate(this.studyEditorContext.studyDetails.endDate?.toString(), DateHelperService.YYYY_MM_DD_DASH_FORMAT);
            }
            this.basicDetails.createdByName = this.studyEditorContext.studyDetails.createdByName;
            this.basicDetails.parentFolderName = this.studyEditorContext.studyDetails.parentFolderName;
            this.basicDetails.parentFolderId = this.studyEditorContext.studyDetails.parentFolderId;
            this.basicDetailsInitialData = {
                ...this.basicDetails
            };
        }
    }

    hasAnyAuthority(permissions: string[]) {
        return this.principal.hasAnyAuthority(permissions);
    }

    autoSaveStudyDetails(field: keyof UpdateStudyBasicDetailsRequest, value: any) {

        // Don't save if the data hasn't changed
        if (JSON.stringify(this.basicDetails) === JSON.stringify(this.basicDetailsInitialData)) {
            return;
        }

        if (this.isFormValid()) {
            // Update the specific field with the new value if provided
            const req: UpdateStudyBasicDetailsRequest = {
                studyId: this.studyId,
                [field]: field.includes('Date') ? this.formatDate(this.basicDetails[field]) : value
            };

            this.studyService.updateStudyBasicDetails(req).toPromise().then(response => {
                this.basicDetailsInitialData[field] = this.basicDetails[field];
                this.studyEditorContext.studyDetails.name = this.basicDetails.name;
            }, () => {
                this.basicDetails[field] = this.basicDetailsInitialData[field];
            });
        }
    }

    changeFolderLocation() {
        const modalRef = this.modalService.open(StudyManagerTreeComponent, {
            modalDialogClass: 'modal-md',
            backdrop: 'static'
        });
        modalRef.componentInstance.selectionMode = 'single';
        modalRef.componentInstance.isFolderSelectionMode = true;
        modalRef.componentInstance.hideViewSummaryButton = true;
        modalRef.componentInstance.showOpenFolderButton = true;
        modalRef.result.then((studies) => {
            if (studies && studies.length === 1) {
                this.basicDetails.parentFolderName = studies[0].name;
                this.basicDetails.parentFolderId = studies[0].id;
                this.triggerAutoSave('parentFolderId', this.basicDetails.parentFolderId);
            }
            this.activeModal.close();
        }, () => {
        });

    }

    isFormValid(): boolean {

        // Validate required fields
        const validationErrors: string[] = [];

        if (!this.basicDetails.name?.trim()) {
            validationErrors.push(this.translateService.instant('study-editor.validation.name-required'));
        }

        if (this.basicDetails?.name && this.basicDetails.name !== this.basicDetailsInitialData.name && this.validateValue(this.basicDetails.name)) {
            validationErrors.push(this.translateService.instant('study-editor.study-dataset.error.name.description.invalid-value'));
        }

        if (!this.basicDetails.description?.trim()) {
            validationErrors.push(this.translateService.instant('study-editor.validation.description-required'));
        }

        if (this.basicDetails?.description && this.basicDetails.description !== this.basicDetailsInitialData.description && this.validateValue(this.basicDetails.description)) {
            validationErrors.push(this.translateService.instant('study-editor.study-dataset.error.name.description.invalid-value'));
        }

        if (!this.basicDetails.studyType) {
            validationErrors.push(this.translateService.instant('study-editor.validation.study-type-required'));
        }

        if (!this.basicDetails.startDate) {
            validationErrors.push(this.translateService.instant('study-editor.validation.creation-date-required'));
        }

        if (!this.dateHelperService.isNgbDateValid(this.basicDetails.startDate)) {
            validationErrors.push(this.translateService.instant('study-editor.validation.creation-date-invalid'));
        }

        if (this.basicDetails.endDate && !this.dateHelperService.isNgbDateValid(this.basicDetails.endDate)) {
            validationErrors.push(this.translateService.instant('study-editor.validation.completion-date-invalid'));
        }

        // Check max length for name and description
        if (this.basicDetails.name?.trim() && this.basicDetails.name.length > 225) {
            validationErrors.push(this.translateService.instant('study-editor.validation.name-too-long'));
        }

        if (this.basicDetails.description?.trim() && this.basicDetails.description.length > 225) {
            validationErrors.push(this.translateService.instant('study-editor.validation.description-too-long'));
        }

        if (this.basicDetails?.objective && this.basicDetails.objective !== this.basicDetailsInitialData.objective && this.validateValue(this.basicDetails.objective)) {
            validationErrors.push(this.translateService.instant('study-editor.study-dataset.error.objective.invalid-value'));
        }

        if (validationErrors.length > 0) {
            // Join all validation messages with line breaks
            const errorMessage = validationErrors.join('<br>');
            this.alertService.error('error.custom', { param: errorMessage, escapeHtml: false });
            return false;
        }

        return true;
    }

    private validateValue(value) {
        const invalidChars = /[<>?|%]/;
        return invalidChars.test(value);
    }

    // Add method to trigger auto-save
    triggerAutoSave(field: keyof UpdateStudyBasicDetailsRequest, value: any) {
        this.autoSaveSubject.next({ field: field, value: value });
    }

    private formatDate(date: any): string | null {
        return date ? this.dateHelperService.convertNgbDateToString(date) : null;
    }

    getStudyType(studyType: string) {
        return 'study-editor.study-type.' + studyType?.toLowerCase().trim().replace(/ /g, '.');
    }

}
